import { inject, Injectable, signal } from '@angular/core';
import {
  BehaviorSubject,
  combineLatest,
  filter,
  map,
  Observable,
  of,
  startWith,
  switchMap,
} from 'rxjs';

import {
  AfterDeletingLastRowBehavior,
  ComparisonQueryFormGroupValue,
  ComplexFilterService,
} from '@blaze-runtime/engine-ui-feature';
import {
  AutocompleteCategory,
  IntelliConfig,
  IntelliSenseService,
  QueryOperatorOption,
} from '@blaze-shared/intellisense-feature';
import {
  ColumnFieldOption,
  findColumn,
  getColumnKey,
  mapColumnsToOptions,
} from '@blaze-shared/schema-domain';
import { SelectOption } from '@blaze-shared/shared-util';
import { GetWorkflowNode, isGetNode } from '@blaze-shared/workflow-domain';

@Injectable()
export class FilterBuilderComplexFilterService implements ComplexFilterService {
  private readonly iss = inject(IntelliSenseService);

  private readonly getNode$ = new BehaviorSubject<GetWorkflowNode | null>(null);
  private readonly getNodeColumns$ = this.getNode$.pipe(
    filter(Boolean),
    switchMap((getNode) => this.iss.getOptionsForColumnsInDataset(getNode.datasetId)),
  );
  private readonly restrictedColumns$ = new BehaviorSubject<string[] | undefined>(undefined);

  readonly showIgnoreWhenEmptyToggle = signal(false);
  afterDeletingLastRow: AfterDeletingLastRowBehavior = 'insert';

  setup(getNode: GetWorkflowNode, restrictedColumns?: string[]): void {
    this.restrictedColumns$.next(restrictedColumns);
    this.getNode$.next(getNode);
  }

  getColumns(): Observable<SelectOption<ColumnFieldOption>[]> {
    return combineLatest({
      columns: this.getNodeColumns$,
      restrictedColumns: this.restrictedColumns$,
    }).pipe(
      map(({ columns, restrictedColumns }) => {
        return mapColumnsToOptions(
          columns.filter(
            (column) =>
              !column.isInternal &&
              !column.isSecure &&
              (!restrictedColumns || restrictedColumns.includes(getColumnKey(column))),
          ),
        );
      }),
    );
  }

  getIntelliConfig(
    queryCondition$: Observable<ComparisonQueryFormGroupValue>,
  ): Observable<IntelliConfig> {
    return combineLatest({
      queryCondition: queryCondition$,
      node: this.getNode$.pipe(filter(Boolean)),
    }).pipe(
      switchMap(({ queryCondition, node }) => {
        return this.iss.createConfigForColumnIdOrName(
          queryCondition?.column ?? '',
          node,
          of(undefined),
          of(undefined),
          of(undefined),
          {
            id: queryCondition.formControlId ?? queryCondition.column,
            operator: queryCondition.operator,
            valueInfo: {
              displayInfos: queryCondition.displayInfos ?? [],
              isFormulaValue: queryCondition.isFormulaValue,
              value: queryCondition.value?.toString() ?? '',
            },
          },
          '',
          !isGetNode(node),
        );
      }),
      map((config) => ({
        ...config,
        autocompleteCategory: AutocompleteCategory.Fixed,
        disableCategoryLabel: true,
        disableGlobalVars: true,
        disableDatasetColumns: true,
        disableFormulas: true,
        hideDataTypeLabels: true,
        showFixedValuesForLinks: true,
      })),
    );
  }

  getOperators(
    queryCondition$: Observable<ComparisonQueryFormGroupValue>,
  ): Observable<QueryOperatorOption[]> {
    return combineLatest({
      queryCondition: queryCondition$.pipe(startWith(null)),
      columns: this.getNodeColumns$,
    }).pipe(
      switchMap(({ queryCondition, columns }) => {
        const column = queryCondition ? findColumn(columns, queryCondition.column) : null;
        return this.iss.getOptionsForQueryOperators(column);
      }),
    );
  }
}
